import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { listMyLicenses, requestSupport } from '@/lib/auth/customer-api'
import { friendlyError } from '@/lib/auth/auth-http'
import { GREY_300, GREY_400, SUCCESS, WHITE, errorBlockStyle, primaryButtonStyle } from '@/lib/genesis'

const MAX_SUBJECT_LEN = 200
const MAX_DETAIL_LEN = 4000

/**
 * ADR-186's portal "Get help" affordance — Genesis's Help System layer 3
 * (400px right drawer, full free-form explanation). Deliberately PULL-only:
 * this component renders nothing unless UserMenu's "Get help" item opens
 * it, no badge or notification ever draws attention to it, and closing it
 * discards any unsaved draft (no persisted state) — matching Genesis's
 * "no tours, no coach marks, no unprompted popups" rule.
 *
 * Instance selector (closes ADR-186 D5's documented gap): uses the
 * existing listMyLicenses customer API — each license row carries
 * instance_id where the license is assigned to a deployed instance. We
 * dedupe those instance IDs into an optional dropdown; the field is
 * skipped entirely when the account has zero assigned instances (most
 * portal accounts today), matching the "no user thinks in DAGs" /
 * progressive-disclosure spirit — don't show a control with nothing to
 * select. The selection (or its absence) flows straight into the existing
 * requestSupport(subject, detail, instanceId) param.
 *
 * Copy is deliberately honest about what happens next (Genesis Error
 * Design's "what to do" principle, applied here to a non-error flow too):
 * this is an async, email-followed-up ticket, not a live chat.
 */
export function GetHelpDrawer({ onClose }: { onClose: () => void }) {
  const [subject, setSubject] = useState('')
  const [detail, setDetail] = useState('')
  const [instanceId, setInstanceId] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)

  const { data: licensesData } = useQuery({
    queryKey: ['portal-my-licenses'],
    queryFn: () => listMyLicenses(),
  })

  const instanceIds = useMemo(() => {
    const ids = (licensesData?.getLicensesList() ?? [])
      .map((lic) => lic.getInstanceId())
      .filter((id): id is string => Boolean(id))
    return Array.from(new Set(ids))
  }, [licensesData])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await requestSupport(subject.trim(), detail.trim(), instanceId || undefined)
      setSubmitted(true)
    } catch (err) {
      setError(friendlyError(err, 'Could not send your request. Please try again.'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60" onClick={onClose}>
      <div
        className="glass-card flex h-full w-full max-w-[400px] flex-col p-5"
        style={{ borderRadius: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between">
          <h2 className="font-sub text-base font-semibold" style={{ color: WHITE }}>
            Get help
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="glass-button rounded-md px-2 py-1 text-sm"
            style={{ color: GREY_300 }}
          >
            Close
          </button>
        </div>

        {submitted ? (
          <div className="flex flex-1 flex-col items-center justify-center text-center">
            <p className="mb-2 text-sm font-medium" style={{ color: SUCCESS }}>
              Request sent.
            </p>
            <p className="mb-4 text-sm" style={{ color: GREY_400 }}>
              Your account team will follow up by email.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-4 py-2 text-sm font-semibold"
              style={primaryButtonStyle}
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-1 flex-col">
            <p className="mb-4 text-sm" style={{ color: GREY_400 }}>
              Describe what you need help with. Your account team will follow up by email — this
              is not a live chat.
            </p>

            <label htmlFor="help-subject" className="mb-1 block text-xs font-medium" style={{ color: GREY_400 }}>
              Subject
            </label>
            <input
              id="help-subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value.slice(0, MAX_SUBJECT_LEN))}
              className="glass-input mb-3 w-full px-3 py-2 text-sm"
              style={{ color: WHITE }}
              placeholder="Short summary, e.g. Can't download my license"
              required
            />

            {instanceIds.length > 0 && (
              <>
                <label
                  htmlFor="help-instance"
                  className="mb-1 block text-xs font-medium"
                  style={{ color: GREY_400 }}
                >
                  Instance (optional)
                </label>
                <select
                  id="help-instance"
                  value={instanceId}
                  onChange={(e) => setInstanceId(e.target.value)}
                  className="glass-input mb-1 w-full px-3 py-2 text-sm"
                  style={{ color: WHITE }}
                >
                  <option value="">Not specific to one instance</option>
                  {instanceIds.map((id) => (
                    <option key={id} value={id}>
                      {id}
                    </option>
                  ))}
                </select>
                <p className="mb-3 text-[11px]" style={{ color: GREY_400 }}>
                  Tell us which deployment this is about, if relevant.
                </p>
              </>
            )}

            <label htmlFor="help-detail" className="mb-1 block text-xs font-medium" style={{ color: GREY_400 }}>
              Detail
            </label>
            <textarea
              id="help-detail"
              value={detail}
              onChange={(e) => setDetail(e.target.value.slice(0, MAX_DETAIL_LEN))}
              className="glass-input mb-1 w-full flex-1 px-3 py-2 text-sm"
              style={{ color: WHITE, minHeight: '160px', resize: 'vertical' }}
              placeholder="What's happening, what you expected, and anything else that would help."
              required
            />
            <p className="mb-3 text-right text-[11px]" style={{ color: GREY_400 }}>
              {detail.length}/{MAX_DETAIL_LEN}
            </p>

            {error && (
              <div className="mb-3 p-2" style={errorBlockStyle}>
                <p className="text-xs" style={{ color: WHITE }}>{error}</p>
              </div>
            )}

            <div className="mt-auto flex gap-2 pt-3">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="glass-button flex-1 rounded-lg py-2 text-sm disabled:opacity-50"
                style={{ color: GREY_300 }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || subject.trim().length === 0 || detail.trim().length === 0}
                className="flex-1 rounded-lg py-2 text-sm font-semibold disabled:opacity-50"
                style={primaryButtonStyle}
              >
                {submitting ? 'Sending…' : 'Send request'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
